import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, ChevronDown, Download, ExternalLink, FileText, Shield, TrendingUp, Home, Upload as UploadIcon, FolderOpen, BarChart3, Settings, LayoutGrid, List } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ContentIcon from '../components/ContentIcon';
import LevelUpHeader from '../components/LevelUpHeader';
import SunsetBanner from '../components/SunsetBanner';
import LevelUpFooter from '../components/LevelUpFooter';

function Browse({ user, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIntro, setShowIntro] = useState(false);
  const [solutionAreas, setSolutionAreas] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    // Load saved preference from localStorage, default to 25
    const saved = localStorage.getItem('browse_items_per_page');
    return saved ? parseInt(saved, 10) : 25;
  });
  const [totalCount, setTotalCount] = useState(0);
  
  // View mode: 'card' (detailed) or 'tile' (compact list)
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('browse_view_mode');
    return saved === 'tile' ? 'tile' : 'card';
  });
  
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('browse_view_mode', mode);
  };
  
  // Default empty filter state
  const defaultFilters = {
    solution_area: [],
    solution_play: [],
    conversation_enterprise_smc: [],
    conversation_smb: [],
    content_type: [],
    target_audience: [],
    language: [],
    search: ''
  };
  
  const [filters, setFilters] = useState(defaultFilters);
  const isEndUser = user && !user.roles.includes('content_owner') && !user.roles.includes('admin') && !user.roles.includes('report_viewer');
  const [expandedFilters, setExpandedFilters] = useState({
    solution_area: true,
    solution_play: false,
    conversation_enterprise_smc: false,
    conversation_smb: false,
    content_type: false,
    target_audience: false,
    language: false
  });
  const [conversationGroups, setConversationGroups] = useState({ 'Conversation (Enterprise & SMC)': [], 'Conversation (SMB)': [] });
  const [solutionPlayInUse, setSolutionPlayInUse] = useState([]);

  // ── Effect 1: On navigation → reset or read URL params (non-taxonomy fields) ─
  useEffect(() => {
    urlAreaHydrated.current = false;
    isParsingUrl.current = true;
    if (location.search) {
      // Apply all fields we can resolve without taxonomy; area/play resolved in Effect 2
      setFilters(parseUrlFilters(location.search, solutionAreas));
    } else {
      setFilters({ ...defaultFilters });
    }
    setTimeout(() => { isParsingUrl.current = false; }, 0);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 2: When taxonomy loads → re-resolve area/play from URL ───────────
  useEffect(() => {
    if (urlAreaHydrated.current) return;
    if (!Object.keys(solutionAreas).length) return;
    urlAreaHydrated.current = true;
    if (!location.search) return;
    const p = new URLSearchParams(location.search);
    if (!p.get('area') && !p.get('play')) return;
    isParsingUrl.current = true;
    setFilters(prev => ({
      ...prev,
      solution_area: parseList(p.get('area') || '', Object.keys(solutionAreas)),
      solution_play: parseList(p.get('play') || '', Object.values(solutionAreas).flat()),
    }));
    setTimeout(() => { isParsingUrl.current = false; }, 0);
  }, [solutionAreas]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isParsingUrl.current) return;
    const qs = filtersToQS(filters);
    const newUrl = `/browse${qs ? `?${qs}` : ''}`;
    const curr = window.location.pathname + window.location.search;
    if (curr !== newUrl) {
      window.history.replaceState(null, '', newUrl);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch taxonomy for solution areas
  useEffect(() => {
    const fetchTaxonomy = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/taxonomy`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setSolutionAreas(data);
        }
      } catch (error) {
        console.error('Failed to load taxonomy:', error);
      }
    };
    fetchTaxonomy();

    const fetchConversationOptions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/conversations`);
        if (response.ok) {
          const data = await response.json();
          setConversationGroups(data.conversations || { 'Conversation (Enterprise & SMC)': [], 'Conversation (SMB)': [] });
        }
      } catch (error) {
        console.error('Failed to load conversation taxonomy:', error);
      }
    };
    fetchConversationOptions();

    const fetchSolutionPlayInUse = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/solution-play-in-use`);
        if (response.ok) {
          const data = await response.json();
          setSolutionPlayInUse(data.solution_play || []);
        }
      } catch (error) {
        console.error('Failed to load solution play in-use values:', error);
      }
    };
    fetchSolutionPlayInUse();
  }, []);

  const contentTypes = ['Presentation', 'Video', 'Excel Sheet', 'PDF', 'Word', 'Caption', 'Web URL'];
  const targetAudiences = ['Sales', 'Presales', 'Technical'];
  const languages = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Japanese', 'Korean', 'Chinese (Simplified)', 'Chinese (Traditional)', 'Italian', 'Russian', 'Turkish', 'Dutch'];

  // ── URL ↔ filter sync utilities ────────────────────────────────────────────

  /** Encode a filter value for URL: lowercase + spaces → hyphens */
  const encodeVal = (v) => v.toLowerCase().replace(/\s+/g, '-');

  /** Decode a URL value back to filter space: hyphens → spaces */
  const decodeVal = (v) => v.trim().replace(/-/g, ' ');

  /** Case-insensitive match of a decoded URL value against canonical option list */
  const matchOption = (val, opts) =>
    opts.find(o => o.toLowerCase() === decodeVal(val).toLowerCase()) || null;

  /** Split comma-separated URL param, resolve each against canonical options */
  const parseList = (raw, opts) =>
    raw ? raw.split(',').map(v => matchOption(v, opts)).filter(Boolean) : [];

  /** Read URL search string → filter state object */
  const parseUrlFilters = (search, areasData = {}) => {
    if (!search) return { ...defaultFilters };
    const p = new URLSearchParams(search);
    const allAreas = Object.keys(areasData);
    const allPlays = Object.values(areasData).flat();
    return {
      search:          p.get('q') ? decodeVal(p.get('q')) : '',
      solution_area:   parseList(p.get('area')     || '', allAreas),
      solution_play:   parseList(p.get('play')     || '', allPlays),
      conversation_enterprise_smc: (p.get('conv_esmc') || '').split(',').filter(Boolean).map(decodeVal),
      conversation_smb:            (p.get('conv_smb') || '').split(',').filter(Boolean).map(decodeVal),
      content_type:    parseList(p.get('type')     || '', contentTypes),
      target_audience: parseList(p.get('audience') || '', targetAudiences),
      language:        parseList(p.get('lang')     || '', languages),
    };
  };

  /** Convert filter state → clean URL query string (hyphens, no %2C comma encoding) */
  const filtersToQS = (f) => {
    const parts = [];
    if (f.search)                 parts.push(`q=${encodeVal(f.search)}`);
    if (f.solution_area.length)   parts.push(`area=${f.solution_area.map(encodeVal).join(',')}`);
    if (f.solution_play.length)   parts.push(`play=${f.solution_play.map(encodeVal).join(',')}`);
    if (f.conversation_enterprise_smc.length) parts.push(`conv_esmc=${f.conversation_enterprise_smc.map(encodeVal).join(',')}`);
    if (f.conversation_smb.length)            parts.push(`conv_smb=${f.conversation_smb.map(encodeVal).join(',')}`);
    if (f.content_type.length)    parts.push(`type=${f.content_type.map(encodeVal).join(',')}`);
    if (f.target_audience.length) parts.push(`audience=${f.target_audience.map(encodeVal).join(',')}`);
    if (f.language.length)        parts.push(`lang=${f.language.map(encodeVal).join(',')}`);
    return parts.join('&');
  };

  // Track whether initial URL hydration has been applied for area/play
  const urlAreaHydrated = useRef(false);
  // Track whether filter change came from URL parse (to avoid echo)
  const isParsingUrl = useRef(false);

  // Language to abbreviation mapping
  const languageAbbreviations = {
    'English': 'EN',
    'Spanish': 'ES',
    'French': 'FR',
    'German': 'DE',
    'Portuguese': 'PT',
    'Japanese': 'JA',
    'Korean': 'KO',
    'Chinese (Simplified)': 'ZH-CN',
    'Chinese (Traditional)': 'ZH-TW',
    'Italian': 'IT',
    'Dutch': 'NL',
    'Russian': 'RU',
    'Arabic': 'AR',
    'Hindi': 'HI',
    'Thai': 'TH',
    'Vietnamese': 'VI',
    'Polish': 'PL',
    'Turkish': 'TR',
    'Swedish': 'SV',
    'Danish': 'DA',
    'Norwegian': 'NO',
    'Finnish': 'FI'
  };

  const getLanguageAbbr = (language) => {
    if (!language) return 'EN';
    return languageAbbreviations[language] || language.substring(0, 2).toUpperCase();
  };

  // AbortController ref to cancel in-flight fetches when filters change
  const abortRef = useRef(null);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
    fetchAssets(1);
  }, [filters, itemsPerPage]);

  useEffect(() => {
    fetchAssets(currentPage);
  }, [currentPage]);

  const fetchAssets = async (page = currentPage) => {
    // Cancel any previous in-flight request
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      // Add pagination parameters
      const skip = (page - 1) * itemsPerPage;
      params.append('skip', skip.toString());
      params.append('limit', itemsPerPage.toString());
      
      // Add search parameter
      if (filters.search) params.append('search', filters.search);
      
      // Add multi-select filters - append each value separately for array support
      if (filters.solution_area.length > 0) {
        filters.solution_area.forEach(area => params.append('solution_area', area));
      }
      if (filters.solution_play.length > 0) {
        filters.solution_play.forEach(play => params.append('solution_play', play));
      }
      if (filters.conversation_enterprise_smc.length > 0) {
        filters.conversation_enterprise_smc.forEach(v => params.append('conversation_enterprise_smc', v));
      }
      if (filters.conversation_smb.length > 0) {
        filters.conversation_smb.forEach(v => params.append('conversation_smb', v));
      }
      if (filters.target_audience.length > 0) {
        filters.target_audience.forEach(audience => params.append('audience', audience));
      }
      if (filters.content_type.length > 0) {
        filters.content_type.forEach(type => params.append('content_type', type));
      }
      if (filters.language.length > 0) {
        filters.language.forEach(lang => params.append('language', lang));
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assets?${params}`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );
      if (signal.aborted) return;
      const data = await response.json();
      if (!signal.aborted) {
        setAssets(data.assets || []);
        setTotalCount(data.total || 0);
      }
    } catch (error) {
      if (error.name === 'AbortError') return; // expected — ignore
      toast.error('Failed to load assets');
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  };

  const toggleFilter = (category, value) => {
    setFilters(prev => {
      const current = prev[category];
      const updated = current.includes(value) 
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [category]: updated };
    });
  };

  const handleItemsPerPageChange = (e) => {
    const newValue = Number(e.target.value);
    setItemsPerPage(newValue);
    setCurrentPage(1);
    // Save user's preference to localStorage
    localStorage.setItem('browse_items_per_page', newValue.toString());
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCount);

  const handleDownload = async (assetId, assetTitle, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/downloads/${assetId}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await response.json();

      if (response.ok) {
        window.open(data.download_url, '_blank');
        toast.success(data.is_weblink ? `Opening: ${assetTitle}` : `Downloading: ${assetTitle}`);
      } else {
        toast.error(data.detail || 'Download failed');
      }
    } catch (error) {
      toast.error('Failed to download asset');
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
      {/* Skip Link for Accessibility */}
      <a 
        href="#main-content" 
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.left = '16px';
          e.target.style.top = '16px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.padding = '8px 16px';
          e.target.style.backgroundColor = '#1B5E5F';
          e.target.style.color = '#FFFFFF';
          e.target.style.zIndex = '9999';
          e.target.style.borderRadius = '4px';
        }}
        onBlur={(e) => {
          e.target.style.left = '-9999px';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
        }}
      >
        Skip to main content
      </a>

      {/* LevelUp Header */}
      <LevelUpHeader user={user} onLogout={onLogout} />
      <SunsetBanner user={user} />

      {/* Main Content */}
      <div style={{ flex: 1 }}>
        {/* Hero Section */}
        <div style={{ background: 'linear-gradient(135deg, #B9DCD2 0%, #E8F5E9 100%)', padding: '60px 24px' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h1 style={{ fontSize: '50px', fontWeight: '600', color: '#1F2937', marginBottom: '8px', lineHeight: '1.2' }}>
              Why Microsoft
            </h1>
            <h2 style={{ fontSize: '32px', fontWeight: '600', color: '#1F2937', margin: '0', lineHeight: '1.2' }}>Compete Enablement for Partners</h2>
          </div>
        </div>

        {/* Intro Section */}
        {showIntro && (
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px' }}>
            <section aria-labelledby="welcome-heading" style={{ backgroundColor: '#1B5E5F', borderRadius: '8px', padding: '32px', marginBottom: '32px' }}>
              <h2 id="welcome-heading" style={{ fontSize: '28px', fontWeight: '600', color: '#FFFFFF', marginBottom: '16px' }}>{t('home.welcome')}</h2>
              <p style={{ fontSize: '16px', color: '#E0E0E0', lineHeight: '1.6' }}>
                {t('home.description')}
              </p>
            </section>

            <section aria-labelledby="benefits-heading" style={{ marginBottom: '32px' }}>
              <h3 id="benefits-heading" style={{ fontSize: '24px', fontWeight: '600', color: '#1F2937', marginBottom: '20px' }}>{t('home.contentAvailable')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                <article style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#D4EC8E', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                    <Shield size={24} color="#1F2937" aria-hidden="true" />
                  </div>
                  <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>{t('home.winDeals')}</h4>
                  <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.5' }}>{t('home.winDealsDesc')}</p>
                </article>
                <article style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#D4EC8E', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                    <TrendingUp size={24} color="#1F2937" aria-hidden="true" />
                  </div>
                  <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>{t('home.accelerateSales')}</h4>
                  <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.5' }}>{t('home.accelerateSalesDesc')}</p>
                </article>
                <article style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#D4EC8E', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                  <FileText size={24} color="#1F2937" aria-hidden="true" />
                </div>
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>{t('home.stayCurrent')}</h4>
                <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.5' }}>{t('home.stayCurrentDesc')}</p>
              </article>
            </div>
          </section>

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <button 
              onClick={() => setShowIntro(false)} 
              style={{ backgroundColor: '#0C6A1D', color: '#FFFFFF', padding: '14px 32px', borderRadius: '4px', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '16px' }}
            >
              {t('home.browseContent')}
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!showIntro && (
        <div id="main-content" style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: isEndUser ? '280px 1fr' : '280px 1fr 280px', gap: '32px' }}>
          {/* Filters Sidebar */}
          <aside aria-label="Content filters" style={{ backgroundColor: '#FFFFFF', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', height: 'fit-content' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>{t('browse.filters')}</h2>
              <button 
                onClick={() => {
                setFilters({ solution_area: [], solution_play: [], conversation_enterprise_smc: [], conversation_smb: [], content_type: [], target_audience: [], language: [], search: '' });
                window.history.replaceState(null, '', '/browse');
              }}
                aria-label="Clear all filters"
                style={{ color: '#0C6A1D', background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {t('browse.clearFilters')}
              </button>
            </div>

            {['solution_area', 'solution_play', 'conversation_enterprise_smc', 'conversation_smb', 'content_type', 'target_audience', 'language'].filter(k => k !== 'solution_play' || solutionPlayInUse.length > 0).map((filterKey) => {
              const filterLabel = filterKey === 'solution_area' ? t('browse.solutionArea') 
                : filterKey === 'solution_play' ? 'Solution Play'
                : filterKey === 'conversation_enterprise_smc' ? 'Conversation (Enterprise\u00A0&\u00A0SMC)'
                : filterKey === 'conversation_smb' ? 'Conversation (SMB)'
                : filterKey === 'content_type' ? t('browse.contentType') 
                : filterKey === 'target_audience' ? t('browse.targetAudience') 
                : t('browse.language');
              const filterId = `filter-${filterKey}`;

              // Compute available plays for Solution Play filter
              let filterOptions;
              if (filterKey === 'solution_area') {
                filterOptions = Object.keys(solutionAreas);
              } else if (filterKey === 'solution_play') {
                filterOptions = solutionPlayInUse;
              } else if (filterKey === 'conversation_enterprise_smc') {
                filterOptions = conversationGroups['Conversation (Enterprise & SMC)'] || [];
              } else if (filterKey === 'conversation_smb') {
                filterOptions = conversationGroups['Conversation (SMB)'] || [];
              } else if (filterKey === 'content_type') {
                filterOptions = contentTypes;
              } else if (filterKey === 'target_audience') {
                filterOptions = targetAudiences;
              } else {
                filterOptions = languages;
              }
              
              return (
                <div key={filterKey} style={{ marginBottom: '24px', paddingTop: filterKey !== 'solution_area' ? '16px' : '0', borderTop: filterKey !== 'solution_area' ? '1px solid #E5E7EB' : 'none' }}>
                  <button 
                    onClick={() => setExpandedFilters(prev => ({ ...prev, [filterKey]: !prev[filterKey] }))} 
                    aria-expanded={expandedFilters[filterKey]}
                    aria-controls={filterId}
                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', padding: '8px 0', cursor: 'pointer', fontSize: '16px', fontWeight: '600', color: '#1F2937', textAlign: 'left' }}
                  >
                    {filterLabel}
                    <ChevronDown 
                      style={{ transform: expandedFilters[filterKey] ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} 
                      size={20} 
                      aria-hidden="true"
                    />
                  </button>
                  {expandedFilters[filterKey] && (
                    <fieldset id={filterId} style={{ border: 'none', padding: 0, margin: 0, marginTop: '12px' }}>
                      <legend className="sr-only">{filterLabel} options</legend>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {filterOptions.map(item => (
                          <label key={item} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151', padding: '4px 0' }}>
                            <input 
                              type="checkbox" 
                              checked={filters[filterKey].includes(item)} 
                              onChange={() => toggleFilter(filterKey, item)} 
                              style={{ accentColor: '#0C6A1D', width: '16px', height: '16px' }} 
                            />
                            <span>{item}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  )}
                </div>
              );
            })}
          </aside>

          {/* Main Content Area */}
          <main role="main" aria-label="Content library" style={{ minWidth: 0 }}>
            {/* Search Bar and Results Info */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <label htmlFor="search-input" className="sr-only">Search assets by title</label>
                  <input
                    id="search-input"
                    type="search"
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    placeholder={t('browse.searchPlaceholder')}
                    data-testid="catalog-search-input"
                    style={{ width: '100%', padding: '12px 40px 12px 16px', border: '2px solid #D1D5DB', borderRadius: '4px', fontSize: '14px', color: '#1F2937' }}
                  />
                  <Search style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6B7280', width: '20px', height: '20px' }} aria-hidden="true" />
                </div>
                <button 
                  onClick={() => fetchAssets(currentPage)} 
                  style={{ backgroundColor: '#0C6A1D', color: '#FFFFFF', padding: '12px 32px', borderRadius: '4px', border: 'none', fontWeight: '600', cursor: 'pointer' }}
                >
                  {t('common.search')}
                </button>
                {/* Toggle + Results count stacked beside the search box */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', marginLeft: '8px' }}>
                  {/* View Mode Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', border: '2px solid #D1D5DB', borderRadius: '4px', padding: '2px' }}>
                    <button
                      onClick={() => handleViewModeChange('card')}
                      aria-label="Card view"
                      title="Card view"
                      data-testid="view-card-btn"
                      style={{
                        padding: '4px 8px',
                        backgroundColor: viewMode === 'card' ? '#0C6A1D' : 'transparent',
                        color: viewMode === 'card' ? '#FFFFFF' : '#4B5563',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.2s'
                      }}
                    >
                      <LayoutGrid size={14} />
                    </button>
                    <button
                      onClick={() => handleViewModeChange('tile')}
                      aria-label="List view"
                      title="List view"
                      data-testid="view-tile-btn"
                      style={{
                        padding: '4px 8px',
                        backgroundColor: viewMode === 'tile' ? '#0C6A1D' : 'transparent',
                        color: viewMode === 'tile' ? '#FFFFFF' : '#4B5563',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.2s'
                      }}
                    >
                      <List size={14} />
                    </button>
                  </div>
                  <span style={{ color: '#4B5563', fontSize: '13px' }} aria-live="polite">
                    {totalCount} {totalCount === 1 ? 'result' : 'results'}
                  </span>
                </div>
              </div>

            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }} role="status" aria-label="Loading content">
                <p style={{ color: '#4B5563' }}>{t('common.loading')}</p>
              </div>
            ) : assets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', backgroundColor: '#FFFFFF', borderRadius: '8px' }}>
                <FileText size={48} color="#9CA3AF" aria-hidden="true" style={{ marginBottom: '16px' }} />
                <p style={{ color: '#4B5563', fontSize: '16px' }}>{t('browse.noResults')}</p>
              </div>
            ) : (
              <>
                {viewMode === 'card' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: isEndUser ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)', gap: '24px' }} role="list" aria-label="Asset cards">
                    {assets.map(asset => (
                      <article
                        key={asset.id}
                        role="listitem"
                        data-testid="catalog-asset-card"
                        style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #E5E7EB', transition: 'box-shadow 0.2s', display: 'flex', flexDirection: 'column' }}
                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.07)'}
                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}
                      >
                        <div style={{ marginBottom: '16px' }}>
                          <ContentIcon type={asset.content_type} />
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', marginBottom: '8px', lineHeight: '1.4' }}>{asset.title}</h3>
                        <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
                          {asset.content_build_date
                            ? <span>Version: <time dateTime={asset.content_build_date}>{new Date(asset.content_build_date).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</time></span>
                            : <span style={{ color: '#9CA3AF' }}>Version: N/A</span>
                          }
                        </p>
                        <p style={{ fontSize: '14px', color: '#4B5563', marginBottom: '16px', lineHeight: '1.5' }}>{asset.description?.substring(0, 100) || 'No description available'}...</p>
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }} aria-label="Target audiences">
                          {asset.target_audiences?.map(audience => (
                            <span key={audience} style={{ fontSize: '11px', padding: '4px 10px', backgroundColor: '#E8F5E9', color: '#166534', borderRadius: '12px', fontWeight: '600' }}>
                              {audience}
                            </span>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '16px', alignItems: 'center' }}>
                          <button 
                            onClick={(e) => handleDownload(asset.id, asset.title, e)} 
                            aria-label={asset.web_url ? `Explore ${asset.title}` : `Download ${asset.title}`}
                            style={{ flex: 1, backgroundColor: '#0C6A1D', color: '#FFFFFF', padding: '10px', borderRadius: '4px', border: 'none', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }}
                          >
                            {asset.web_url ? <ExternalLink size={16} aria-hidden="true" /> : <Download size={16} aria-hidden="true" />}
                            {asset.web_url ? 'Explore' : t('common.download')}
                          </button>
                          <span 
                            title={asset.language || 'English'} 
                            style={{ fontSize: '11px', color: '#6B7280', padding: '6px 10px', backgroundColor: '#F3F4F6', borderRadius: '3px', fontWeight: '600' }}
                          >
                            {getLanguageAbbr(asset.language)}
                          </span>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', maxWidth: '100%' }} role="list" aria-label="Asset tiles">
                    {assets.map(asset => (
                      <article
                        key={asset.id}
                        role="listitem"
                        data-testid="catalog-asset-tile"
                        style={{ 
                          backgroundColor: '#FFFFFF', 
                          borderRadius: '6px', 
                          padding: '12px 16px', 
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04)', 
                          border: '1px solid #E5E7EB', 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          transition: 'box-shadow 0.2s',
                          width: '100%',
                          maxWidth: '100%',
                          boxSizing: 'border-box'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.06)'}
                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)'}
                      >
                        {/* Icon */}
                        <div style={{ flexShrink: 0 }}>
                          <ContentIcon type={asset.content_type} />
                        </div>
                        
                        {/* Title + Audiences */}
                        <div style={{ flex: '1 1 0', minWidth: 0 }}>
                          <h3 style={{ 
                            fontSize: '14px', 
                            fontWeight: '600', 
                            color: '#1F2937', 
                            margin: 0,
                            lineHeight: '1.4',
                            wordBreak: 'break-word'
                          }}>{asset.title}</h3>
                          {asset.target_audiences?.length > 0 && (
                            <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                              {asset.target_audiences.slice(0, 3).map(audience => (
                                <span key={audience} style={{ fontSize: '10px', padding: '2px 8px', backgroundColor: '#E8F5E9', color: '#166534', borderRadius: '10px', fontWeight: '600' }}>
                                  {audience}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Version Date */}
                        <div style={{ flexShrink: 0, minWidth: '100px', textAlign: 'right', fontSize: '12px', color: '#6B7280' }}>
                          <div>Version</div>
                          <div>
                            {asset.content_build_date
                              ? new Date(asset.content_build_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                              : 'N/A'
                            }
                          </div>
                        </div>
                        
                        {/* Language */}
                        <span 
                          title={asset.language || 'English'} 
                          style={{ flexShrink: 0, fontSize: '11px', color: '#6B7280', padding: '4px 8px', backgroundColor: '#F3F4F6', borderRadius: '3px', fontWeight: '600', minWidth: '36px', textAlign: 'center' }}
                        >
                          {getLanguageAbbr(asset.language)}
                        </span>
                        
                        {/* Download / Explore Button */}
                        <button 
                          onClick={(e) => handleDownload(asset.id, asset.title, e)} 
                          aria-label={asset.web_url ? `Explore ${asset.title}` : `Download ${asset.title}`}
                          style={{ 
                            flexShrink: 0,
                            backgroundColor: '#0C6A1D', 
                            color: '#FFFFFF', 
                            padding: '8px 16px', 
                            borderRadius: '4px', 
                            border: 'none', 
                            fontWeight: '600', 
                            fontSize: '13px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px', 
                            cursor: 'pointer' 
                          }}
                        >
                          {asset.web_url ? <ExternalLink size={14} aria-hidden="true" /> : <Download size={14} aria-hidden="true" />}
                          {asset.web_url ? 'Explore' : t('common.download')}
                        </button>
                      </article>
                    ))}
                  </div>
                )}

                {/* Pagination Row: Results per page (left) | Prev/Page/Next (center) | Showing X-Y (right) */}
                {totalCount > 0 && (
                  <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <label htmlFor="items-per-page" style={{ color: '#4B5563', fontSize: '14px', whiteSpace: 'nowrap' }}>Results per page:</label>
                      <select
                        id="items-per-page"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        style={{ padding: '6px 8px', border: '2px solid #D1D5DB', borderRadius: '4px', fontSize: '14px', color: '#1F2937', backgroundColor: '#FFFFFF', cursor: 'pointer' }}
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                      </select>
                    </div>
                    {totalPages > 1 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          aria-label="Previous page"
                          style={{
                            padding: '10px 12px',
                            minWidth: '90px',
                            backgroundColor: currentPage === 1 ? '#E5E7EB' : '#0C6A1D',
                            color: currentPage === 1 ? '#9CA3AF' : '#FFFFFF',
                            border: 'none',
                            borderRadius: '4px',
                            fontWeight: '600',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Previous
                        </button>
                        <span style={{ color: '#4B5563', fontSize: '14px', fontWeight: '500' }}>
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          aria-label="Next page"
                          style={{
                            padding: '10px 12px',
                            minWidth: '90px',
                            backgroundColor: currentPage === totalPages ? '#E5E7EB' : '#0C6A1D',
                            color: currentPage === totalPages ? '#9CA3AF' : '#FFFFFF',
                            border: 'none',
                            borderRadius: '4px',
                            fontWeight: '600',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Next
                        </button>
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', flex: 1 }}>
                      <span style={{ color: '#4B5563', fontSize: '14px' }} aria-live="polite">
                        Showing {startIndex}-{endIndex} of {totalCount} results
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </main>

          {/* Right Sidebar — Quick Links (privileged roles only) */}
          {!isEndUser && (
          <aside aria-label="Quick links" style={{ height: 'fit-content' }}>
            <nav style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '16px' }}>Quick Links</h2>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {(user.roles.includes('content_owner') || user.roles.includes('admin')) && (
                    <>
                      <li>
                        <button onClick={() => navigate('/upload')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#1F2937', fontWeight: '500', width: '100%' }}>
                          <UploadIcon size={18} color="#0C6A1D" aria-hidden="true" />
                          <span>{t('nav.uploadContent')}</span>
                        </button>
                      </li>
                      <li>
                        <button onClick={() => navigate('/my-content')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#1F2937', fontWeight: '500', width: '100%' }}>
                          <FolderOpen size={18} color="#0C6A1D" aria-hidden="true" />
                          <span>{t('nav.myContent')}</span>
                        </button>
                      </li>
                    </>
                  )}
                  {user.roles.includes('admin') && (
                    <li>
                      <button onClick={() => navigate('/admin')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#1F2937', fontWeight: '500', width: '100%' }}>
                        <Settings size={18} color="#0C6A1D" aria-hidden="true" />
                        <span>{t('nav.admin')}</span>
                      </button>
                    </li>
                  )}
                  {(user.roles.includes('admin') || user.roles.includes('report_viewer')) && (
                    <li>
                      <button onClick={() => navigate('/analytics')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#1F2937', fontWeight: '500', width: '100%' }}>
                        <BarChart3 size={18} color="#0C6A1D" aria-hidden="true" />
                        <span>{t('nav.analytics')}</span>
                      </button>
                    </li>
                  )}
                </ul>
              </nav>
          </aside>
          )}
        </div>
      )}
      </div>

      {/* LevelUp Footer */}
      <LevelUpFooter />

      {/* Hidden styles for screen readers */}
      <style>{`
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `}</style>
    </div>
  );
}

export default Browse;
